import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  FileText, ArrowLeft, ArrowRight, ChevronLeft,
  CheckCircle, Building2, Package, TrendingUp, Download, QrCode, Loader2,
} from 'lucide-react';

import Step1LabPicker from '../../components/contract-automation/Step1LabPicker';
import Step2ProductsCommissions from '../../components/contract-automation/Step2ProductsCommissions';
import Step3ContractFinalize from '../../components/contract-automation/Step3ContractFinalize';
import ProductForm from '../../components/laboratory/forms/ProductForm';

import contractAutomationApi from '../../api/contract-automation-api';
import { GET_PRODUCTS_BY_LABORATORY, GET_PROMOTIONS_BY_LABORATORY, GENERATE_QR_CODES, ensureCompleteProductData } from '../../api/laboratory-api';
import { GET_REGIONS } from '../../api/regions-api';

const STEPS = [
  { number: 1, label: 'Laboratoire' },
  { number: 2, label: 'Produits & Commissions' },
  { number: 3, label: 'Finalisation' },
];

const ContractAutomationPage = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [selectedLaboratory, setSelectedLaboratory] = useState(null);
  const [laboratoryProducts, setLaboratoryProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [promotionMap, setPromotionMap] = useState({});
  const [contractData, setContractData] = useState({
    nom: '', number: '', dateStart: '', dateEnd: '',
  });

  const [generatedPdfBase64, setGeneratedPdfBase64] = useState(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [createdContractId, setCreatedContractId] = useState(null);
  const [createdPromotions, setCreatedPromotions] = useState([]);
  const [qrLoadingId, setQrLoadingId] = useState(null);

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const { data: regionsData } = useQuery(GET_REGIONS, { fetchPolicy: 'cache-first' });

  const [generateQRCodes] = useMutation(GENERATE_QR_CODES);

  const { data: existingPromosData } = useQuery(GET_PROMOTIONS_BY_LABORATORY, {
    variables: { laboratoryId: selectedLaboratory?.id },
    skip: !selectedLaboratory?.id,
    fetchPolicy: 'network-only',
  });
  const existingPromotions = existingPromosData?.promotionsByLaboratory || [];
  const regions = regionsData?.regions ?? [];
  const promotionsArray = selectedProducts.map(p => promotionMap[p.id]).filter(Boolean);

  const [fetchProducts, { loading: productsQueryLoading }] = useLazyQuery(
    GET_PRODUCTS_BY_LABORATORY,
    {
      fetchPolicy: 'cache-and-network',
      onCompleted: data => {
        if (data?.productsByLaboratory) {
          const products = data.productsByLaboratory
            .map(p => (ensureCompleteProductData ? ensureCompleteProductData([p])[0] : p))
            .filter(Boolean);
          setLaboratoryProducts(products);
          setSelectedLaboratory(prev => (prev ? { ...prev, products } : prev));
        }
        setIsLoadingProducts(false);
      },
      onError: () => {
        toast.error('Erreur lors du chargement des produits');
        setIsLoadingProducts(false);
      },
    }
  );

  useEffect(() => {
    if (selectedLaboratory && !contractData.number) {
      setContractData({
        nom: `Contrat de partenariat - ${selectedLaboratory.nom}`,
        number: contractAutomationApi.generateContractNumber(selectedLaboratory.nom),
        dateStart: '',
        dateEnd: '',
      });
    }
  }, [selectedLaboratory]);

  // Auto-compute contract dates from promotions:
  // dateStart = earliest startDate, dateEnd = latest endDate
  useEffect(() => {
    if (promotionsArray.length === 0) return;
    const starts = promotionsArray.map(p => p.startDate).filter(Boolean).sort();
    const ends   = promotionsArray.map(p => p.endDate).filter(Boolean).sort();
    if (starts.length && ends.length) {
      setContractData(prev => ({
        ...prev,
        dateStart: starts[0],
        dateEnd: ends[ends.length - 1],
      }));
    }
  }, [promotionMap]);

  useEffect(() => {
    if (selectedLaboratory?.id) {
      setIsLoadingProducts(true);
      setLaboratoryProducts([]);
      fetchProducts({ variables: { laboratoryId: selectedLaboratory.id } });
    }
  }, [selectedLaboratory?.id]);

  const handleLaboratorySelect = lab => {
    setSelectedLaboratory(lab);
    setSelectedProducts([]);
    setPromotionMap({});
    setContractData({ nom: '', number: '', dateStart: '', dateEnd: '' });
    setGeneratedPdfBase64(null);
    setGeneratedPdfUrl(null);
    setError(null);
  };

  const handleProductToggle = product => {
    const isSelected = selectedProducts.some(p => p.id === product.id);
    if (isSelected) {
      setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
      setPromotionMap(prev => { const { [product.id]: _, ...rest } = prev; return rest; });
    } else {
      const today = contractData.dateStart || new Date().toISOString().split('T')[0];
      const nextYear =
        contractData.dateEnd ||
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      setSelectedProducts(prev => [...prev, product]);
      setPromotionMap(prev => ({
        ...prev,
        [product.id]: {
          productId: product.id,
          productName: product.name,
          name: `Promotion ${product.name}`,
          commissionType: 'FIXED_PERCENTAGE',
          commissionAmount: 0,
          commissionPercentage: 0,
          quantity: 1000,
          startDate: today,
          endDate: nextYear,
          regionIds: [],
          allowedUserTypes: [],
        },
      }));
    }
    setGeneratedPdfBase64(null);
    setGeneratedPdfUrl(null);
  };

  const handlePromotionUpdate = (productId, updated) => {
    setPromotionMap(prev => ({ ...prev, [productId]: updated }));
    setGeneratedPdfBase64(null);
    setGeneratedPdfUrl(null);
  };

  const handleContractDataChange = (field, value) => {
    setContractData(prev => ({ ...prev, [field]: value }));
    if (field === 'dateStart' || field === 'dateEnd') {
      setGeneratedPdfBase64(null);
      setGeneratedPdfUrl(null);
    }
  };

  const handleGeneratePdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const result = await contractAutomationApi.generateContractPdf({
        laboratoryId: selectedLaboratory.id,
        productIds: selectedProducts.map(p => p.id),
        contractInfo: contractData,
        promotions: promotionsArray,
      });
      if (result.success) {
        setGeneratedPdfUrl(result.downloadUrl);
        setGeneratedPdfBase64(result.pdfBase64);
        toast.success('PDF généré avec succès');
      } else {
        toast.error(result.message || 'Erreur lors de la génération du PDF');
      }
    } catch {
      toast.error('Erreur technique lors de la génération du PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleCreateContract = async () => {
    setIsLoading(true);
    try {
      const result = await contractAutomationApi.createAutomatedContract({
        laboratoryId: selectedLaboratory.id,
        productIds: selectedProducts.map(p => p.id),
        contractInfo: contractData,
        promotions: promotionsArray,
        pdfBase64: generatedPdfBase64,
      });
      if (result.success) {
        setCreatedContractId(result.contractId);
        setCreatedPromotions(result.promotions || []);
        setDone(true);
        toast.success('Contrat créé avec succès');
      } else {
        toast.error(result.message || 'Erreur lors de la création du contrat');
      }
    } catch {
      toast.error('Erreur technique lors de la création du contrat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!generatedPdfBase64) return;
    try {
      const bytes = atob(generatedPdfBase64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrat-${contractData.number || 'mazid'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Preview: always use base64 blob — avoids 404 from server URL
  const handlePreviewPdf = () => {
    if (!generatedPdfBase64) return;
    try {
      const bytes = atob(generatedPdfBase64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch {
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  const handleGenerateQR = async (promotion) => {
    setQrLoadingId(promotion.id);
    try {
      const { data } = await generateQRCodes({
        variables: { promotionId: promotion.id, quantity: promotion.quantity },
      });
      const result = data?.generateQRCodes;
      if (!result?.success) {
        toast.error(result?.message || 'Erreur lors de la génération');
        return;
      }
      if (result.excelBase64) {
        const bytes = atob(result.excelBase64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.excelFilename || `qrcodes-${promotion.name}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
      if (result.pdfBase64) {
        const bytes = atob(result.pdfBase64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || `qrcodes-${promotion.name}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`QR codes générés : ${result.count}`);
    } catch (err) {
      toast.error('Erreur technique lors de la génération des QR codes');
    } finally {
      setQrLoadingId(null);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setSelectedLaboratory(null);
    setLaboratoryProducts([]);
    setSelectedProducts([]);
    setPromotionMap({});
    setContractData({ nom: '', number: '', dateStart: '', dateEnd: '' });
    setGeneratedPdfBase64(null);
    setGeneratedPdfUrl(null);
    setCreatedContractId(null);
    setCreatedPromotions([]);
    setQrLoadingId(null);
    setDone(false);
    setError(null);
  };

  const handleNext = () => {
    setError(null);
    if (currentStep === 1 && !selectedLaboratory) {
      setError('Veuillez sélectionner un laboratoire');
      return;
    }
    if (currentStep === 2) {
      if (selectedProducts.length === 0) {
        setError('Veuillez sélectionner au moins un produit');
        return;
      }
      const invalid = promotionsArray.some(p => !p.name || (p.commissionAmount || 0) <= 0);
      if (invalid) {
        setError('Veuillez vérifier les commissions (valeur vendeur > 0 requise)');
        return;
      }
      // Check date conflicts with existing promotions for this laboratory
      const conflictingProducts = selectedProducts.filter(product => {
        const promo = promotionMap[product.id];
        if (!promo?.startDate || !promo?.endDate) return false;
        const newStart = new Date(promo.startDate);
        const newEnd   = new Date(promo.endDate);
        return existingPromotions.some(ep => {
          if (!ep.products?.some(p => p.id === product.id)) return false;
          return newStart <= new Date(ep.endDate) && newEnd >= new Date(ep.startDate);
        });
      });
      if (conflictingProducts.length > 0) {
        setError(
          `Conflit de dates pour ${conflictingProducts.length} produit(s) : ${conflictingProducts.map(p => p.name).join(', ')}. Une promotion active existe déjà pour ces produits sur cette période.`
        );
        return;
      }
    }
    setCurrentStep(s => Math.min(s + 1, STEPS.length));
  };

  const handlePrev = () => {
    setError(null);
    setCurrentStep(s => Math.max(s - 1, 1));
  };

  const handleProductModalSuccess = async () => {
    setIsProductModalOpen(false);
    setEditingProduct(null);
    if (selectedLaboratory?.id) {
      setIsLoadingProducts(true);
      await fetchProducts({ variables: { laboratoryId: selectedLaboratory.id } });
    }
  };

  const isNextDisabled =
    (currentStep === 1 && !selectedLaboratory) ||
    (currentStep === 2 && selectedProducts.length === 0) ||
    (isLoadingProducts && currentStep === 2);

  // ── Success screen ────────────────────────────────────────────────────────

  if (done) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center p-6 overflow-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 max-w-lg w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Contrat créé !</h2>
          <p className="text-gray-500 text-sm mb-1">{contractData.nom}</p>
          <p className="text-xs text-gray-400 font-mono mb-6">{contractData.number}</p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-gray-500">
                <Building2 size={13} /> Laboratoire
              </span>
              <span className="font-semibold text-gray-900">{selectedLaboratory?.nom}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-gray-500">
                <Package size={13} /> Produits
              </span>
              <span className="font-semibold text-gray-900">{selectedProducts.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-gray-500">
                <TrendingUp size={13} /> Période
              </span>
              <span className="font-semibold text-gray-900">
                {contractData.dateStart} → {contractData.dateEnd}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            {generatedPdfBase64 && (
              <button
                onClick={handleDownloadPdf}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Download size={14} /> PDF
              </button>
            )}
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-2.5 bg-blue-600 rounded-xl text-sm font-medium text-white hover:bg-blue-700"
            >
              Nouveau contrat
            </button>
          </div>

          {createdPromotions.length > 0 && (
            <div className="mt-6 text-left">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <QrCode size={14} /> Générer les QR codes
              </h3>
              <div className="space-y-2">
                {createdPromotions.map(promo => (
                  <div key={promo.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{promo.name}</p>
                      <p className="text-xs text-gray-400">{promo.quantity} codes</p>
                    </div>
                    <button
                      onClick={() => handleGenerateQR(promo)}
                      disabled={!!qrLoadingId}
                      className="ml-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {qrLoadingId === promo.id ? (
                        <><Loader2 size={12} className="animate-spin" /> Génération...</>
                      ) : (
                        <><QrCode size={12} /> Générer</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/admin/laboratories')}
            className="mt-3 w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Retour aux laboratoires
          </button>
        </div>
      </div>
    );
  }

  // ── Main wizard — NO page scroll ──────────────────────────────────────────

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 py-6 flex flex-col flex-1 min-h-0">

        {/* Header — fixed */}
        <div className="flex items-center gap-3 mb-5 flex-shrink-0">
          <button
            onClick={() => currentStep > 1 ? handlePrev() : navigate('/admin/laboratories')}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-white transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText size={18} className="text-blue-600" />
              Nouveau contrat
            </h1>
            {selectedLaboratory && (
              <p className="text-xs text-gray-400 mt-0.5">{selectedLaboratory.nom}</p>
            )}
          </div>
        </div>

        {/* Step progress — fixed */}
        <div className="flex items-center mb-5 flex-shrink-0">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.number}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors flex-shrink-0 ${
                    currentStep > step.number
                      ? 'bg-green-500 text-white'
                      : currentStep === step.number
                        ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                        : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {currentStep > step.number ? <CheckCircle size={13} /> : step.number}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${
                    currentStep >= step.number ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-3 transition-colors ${
                    currentStep > step.number ? 'bg-green-400' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error banner — fixed */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2 flex-shrink-0">
            <span className="text-red-400 font-bold flex-shrink-0">!</span>
            {error}
          </div>
        )}

        {/* Card — fills remaining height, no outer scroll */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col flex-1 min-h-0">

          {/* Step content — fills card height, no outer scroll */}
          <div className="flex-1 min-h-0 p-6 sm:p-8 flex flex-col">
            {currentStep === 1 && (
              <Step1LabPicker
                selectedLaboratory={selectedLaboratory}
                onLaboratorySelect={handleLaboratorySelect}
                className="flex-1 min-h-0"
              />
            )}
            {currentStep === 2 && (
              <Step2ProductsCommissions
                selectedLaboratory={{ ...selectedLaboratory, products: laboratoryProducts }}
                selectedProducts={selectedProducts}
                promotions={promotionMap}
                regions={regions}
                existingPromotions={existingPromotions}
                isLoadingProducts={isLoadingProducts || productsQueryLoading}
                onProductToggle={handleProductToggle}
                onPromotionUpdate={handlePromotionUpdate}
                onCreateProduct={() => setIsProductModalOpen(true)}
                onEditProduct={p => {
                  setEditingProduct(p);
                  setIsProductModalOpen(true);
                }}
              />
            )}
            {currentStep === 3 && (
              <Step3ContractFinalize
                selectedLaboratory={selectedLaboratory}
                selectedProducts={selectedProducts}
                promotions={promotionsArray}
                contractData={contractData}
                generatedPdfBase64={generatedPdfBase64}
                isGeneratingPdf={isGeneratingPdf}
                isCreating={isLoading}
                onContractDataChange={handleContractDataChange}
                onGeneratePdf={handleGeneratePdf}
                onCreateContract={handleCreateContract}
                onDownloadPdf={handleDownloadPdf}
                onPreviewPdf={handlePreviewPdf}
              />
            )}
          </div>

          {/* Navigation footer — pinned to bottom */}
          <div className="px-6 sm:px-8 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50/70 rounded-b-2xl flex-shrink-0">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-500 rounded-xl hover:bg-white hover:text-gray-900 border border-transparent hover:border-gray-200 disabled:opacity-0 disabled:pointer-events-none transition-all"
            >
              <ArrowLeft size={15} />
              Précédent
            </button>

            <div className="flex items-center gap-1.5">
              {STEPS.map(s => (
                <div
                  key={s.number}
                  className={`rounded-full transition-all ${
                    currentStep === s.number
                      ? 'w-4 h-2 bg-blue-600'
                      : currentStep > s.number
                        ? 'w-2 h-2 bg-green-400'
                        : 'w-2 h-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {currentStep < STEPS.length ? (
              <button
                onClick={handleNext}
                disabled={isNextDisabled}
                className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                Suivant
                <ArrowRight size={15} />
              </button>
            ) : (
              <div className="w-24" />
            )}
          </div>
        </div>
      </div>

      <ProductForm
        laboratory={selectedLaboratory}
        product={editingProduct}
        isOpen={isProductModalOpen}
        onClose={() => {
          setIsProductModalOpen(false);
          setEditingProduct(null);
        }}
        onSuccess={handleProductModalSuccess}
      />
    </div>
  );
};

export default ContractAutomationPage;
